import React, { useEffect, useState } from 'react';
import './TrustedLogosPage.css';
import { authHeader } from '../services/authService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend.ouest-pro-event.fr';

const buildImageUrl = (relativeUrl) => {
  if (!relativeUrl) {
    return '';
  }
  if (relativeUrl.startsWith('http')) {
    return relativeUrl;
  }
  return `${BACKEND_URL}${relativeUrl}`;
};

function TrustedLogosPage() {
  const [logos, setLogos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [newLogo, setNewLogo] = useState({ name: '', link: '' });
  const [newLogoFile, setNewLogoFile] = useState(null);
  const [fileInputs, setFileInputs] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchLogos = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/admin/trusted-logos`, {
        headers: {
          ...authHeader(),
        },
      });
      if (!response.ok) {
        throw new Error("Impossible de récupérer les logos");
      }
      const data = await response.json();
      setLogos(data.logos || []);
    } catch (err) {
      setError(err.message || "Une erreur est survenue");
      setLogos([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogos();
  }, []);

  const handleNewLogoChange = (event) => {
    const { name, value } = event.target;
    setNewLogo((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewLogoFileChange = (event) => {
    const file = event.target.files?.[0];
    setNewLogoFile(file || null);
  };

  const resetFeedback = () => {
    setFeedback(null);
    setError(null);
  };

  const handleCreateLogo = async (event) => {
    event.preventDefault();
    resetFeedback();

    if (!newLogoFile) {
      setError("Veuillez sélectionner une image.");
      return;
    }

    setIsCreating(true);
    try {
      const formData = new FormData();
      formData.append('image', newLogoFile);
      formData.append('name', newLogo.name);
      formData.append('link', newLogo.link);

      const response = await fetch(`${BACKEND_URL}/admin/trusted-logos`, {
        method: 'POST',
        headers: {
          ...authHeader(),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Impossible d'ajouter le logo");
      }

      await fetchLogos();
      setNewLogo({ name: '', link: '' });
      setNewLogoFile(null);
      setShowAddForm(false);
      setFeedback('Logo ajouté avec succès.');
    } catch (err) {
      setError(err.message || "Une erreur est survenue lors de l'ajout du logo");
    } finally {
      setIsCreating(false);
    }
  };

  const handleLogoFieldChange = (id, field, value) => {
    setLogos((prev) =>
      prev.map((logo) =>
        logo.id === id
          ? {
              ...logo,
              [field]: value,
            }
          : logo
      )
    );
  };

  const handleLogoFileChange = (id, file) => {
    setFileInputs((prev) => ({
      ...prev,
      [id]: file || null,
    }));
  };

  const handleUpdateLogo = async (logo) => {
    resetFeedback();
    setUpdatingId(logo.id);

    try {
      const formData = new FormData();
      formData.append('name', logo.name || '');
      formData.append('link', logo.link || '');
      if (fileInputs[logo.id]) {
        formData.append('image', fileInputs[logo.id]);
      }

      const response = await fetch(`${BACKEND_URL}/admin/trusted-logos/${logo.id}`, {
        method: 'PUT',
        headers: {
          ...authHeader(),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Impossible de mettre à jour le logo");
      }

      await fetchLogos();
      setFileInputs((prev) => ({ ...prev, [logo.id]: null }));
      setFeedback('Logo mis à jour avec succès.');
    } catch (err) {
      setError(err.message || "Une erreur est survenue lors de la mise à jour");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteLogo = async (id) => {
    resetFeedback();
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce logo ?')) {
      return;
    }

    setUpdatingId(id);
    try {
      const response = await fetch(`${BACKEND_URL}/admin/trusted-logos/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Impossible de supprimer le logo");
      }

      await fetchLogos();
      setFeedback('Logo supprimé avec succès.');
    } catch (err) {
      setError(err.message || "Une erreur est survenue lors de la suppression");
    } finally {
      setUpdatingId(null);
    }
  };

  const persistPositions = async (updatedLogos) => {
    try {
      const response = await fetch(`${BACKEND_URL}/admin/trusted-logos/positions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
        body: JSON.stringify({
          logos: updatedLogos.map((logo) => ({ id: logo.id })),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'La mise à jour de l\'ordre a échoué');
      }

      const data = await response.json();
      setLogos(data.logos || updatedLogos);
    } catch (err) {
      setError(err.message || 'La mise à jour de l\'ordre a échoué');
    }
  };

  const handleMoveLogo = (index, direction) => {
    resetFeedback();
    setError(null);
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= logos.length) {
      return;
    }

    const updatedLogos = [...logos];
    const [movedLogo] = updatedLogos.splice(index, 1);
    updatedLogos.splice(newIndex, 0, movedLogo);
    setLogos(updatedLogos);
    persistPositions(updatedLogos);
  };

  return (
    <div className="trusted-logos-page">
      {/* En-tête avec statistiques */}
      <header className="trusted-logos-header">
        <div className="header-content">
          <h1>
            <span className="header-icon">🏢</span>
            Logos clients

          </h1>
         
        </div>
        <div className="header-stats">
          <div className="stat-card">
            <span className="stat-number">{logos.length}</span>
            <span className="stat-label">Logo{logos.length > 1 ? 's' : ''} actif{logos.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </header>

      {/* Notifications */}
      {(error || feedback) && (
        <div className={`notification ${error ? 'notification-error' : 'notification-success'}`}>
          <div className="notification-content">
            <span className="notification-icon">
              {error ? '❌' : '✅'}
            </span>
            <span className="notification-text">{error || feedback}</span>
          </div>
          <button 
            className="notification-close"
            onClick={resetFeedback}
          >
            ×
          </button>
        </div>
      )}

      {/* Bouton d'ajout */}
      <div className="add-logo-section">
        <button 
          className={`add-logo-toggle ${showAddForm ? 'active' : ''}`}
          onClick={() => setShowAddForm(!showAddForm)}
        >
          <span className="button-icon">➕</span>
          Ajouter un nouveau logo
        </button>

        {/* Formulaire d'ajout */}
        {showAddForm && (
          <div className="add-logo-form">
            <div className="form-header">
              <h3>Nouveau logo</h3>
              <button 
                className="close-form"
                onClick={() => setShowAddForm(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateLogo}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="newLogoName">
                    <span className="label-icon">🏷️</span>
                    Nom de l'entreprise
                  </label>
                  <input
                    id="newLogoName"
                    name="name"
                    type="text"
                    value={newLogo.name}
                    onChange={handleNewLogoChange}
                    placeholder="Ex: EAG, Maison Feger..."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="newLogoLink">
                    <span className="label-icon">🔗</span>
                    Site web (optionnel)
                  </label>
                  <input
                    id="newLogoLink"
                    name="link"
                    type="url"
                    value={newLogo.link}
                    onChange={handleNewLogoChange}
                    placeholder="https://exemple.com"
                  />
                </div>
              </div>
              <div className="form-group file-upload">
                <label htmlFor="newLogoFile">
                  <span className="label-icon">📷</span>
                  Image du logo
                </label>
                <input
                  id="newLogoFile"
                  type="file"
                  accept="image/*"
                  onChange={handleNewLogoFileChange}
                  required
                />
                <p className="form-hint">Formats acceptés: JPG, PNG, SVG (max 5MB)</p>
              </div>
              <div className="form-actions">
                <button 
                  type="button" 
                  className="button-secondary"
                  onClick={() => setShowAddForm(false)}
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  className="button-primary"
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <>
                      <span className="spinner"></span>
                      Ajout en cours...
                    </>
                  ) : (
                    <>
                      <span className="button-icon">✨</span>
                      Ajouter le logo
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Liste des logos */}
      <section className="logos-list-section">
        <div className="section-header">
          <h2>Logos existants</h2>
          {logos.length > 0 && (
            <p className="section-subtitle">
              Glissez pour réorganiser • Cliquez pour modifier
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Chargement des logos...</p>
          </div>
        ) : logos.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📷</div>
            <h3>Aucun logo pour le moment</h3>
          </div>
        ) : (
          <div className="logos-grid">
            {logos.map((logo, index) => (
              <div key={logo.id} className="logo-card">
                <div className="logo-preview">
                  {logo.image_url ? (
                    <img src={buildImageUrl(logo.image_url)} alt={logo.name || 'Logo'} />
                  ) : (
                    <div className="logo-placeholder">
                      <span>🖼️</span>
                    </div>
                  )}
                </div>

                <div className="logo-info">
                  <div className="logo-fields">
                    <div className="field-group">
                      <label>Nom</label>
                      <input
                        type="text"
                        value={logo.name || ''}
                        onChange={(e) => handleLogoFieldChange(logo.id, 'name', e.target.value)}
                        placeholder="Nom de l'entreprise"
                      />
                    </div>
                    <div className="field-group">
                      <label>Lien web</label>
                      <input
                        type="url"
                        value={logo.link || ''}
                        onChange={(e) => handleLogoFieldChange(logo.id, 'link', e.target.value)}
                        placeholder="https://exemple.com"
                      />
                    </div>
                    <div className="field-group">
                      <label>Remplacer l'image</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoFileChange(logo.id, e.target.files?.[0] || null)}
                      />
                    </div>
                  </div>

                  <div className="logo-actions">
                    <div className="position-controls">
                      <button
                        type="button"
                        className="position-btn"
                        onClick={() => handleMoveLogo(index, -1)}
                        disabled={index === 0 || updatingId === logo.id}
                        title="Déplacer vers le haut"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="position-btn"
                        onClick={() => handleMoveLogo(index, 1)}
                        disabled={index === logos.length - 1 || updatingId === logo.id}
                        title="Déplacer vers le bas"
                      >
                        ↓
                      </button>
                    </div>

                    <div className="main-actions">
                      <button
                        type="button"
                        className="button-primary"
                        onClick={() => handleUpdateLogo(logo)}
                        disabled={updatingId === logo.id}
                      >
                        {updatingId === logo.id ? (
                          <>
                            <span className="spinner"></span>
                            Sauvegarde...
                          </>
                        ) : (
                          <>
                            <span className="button-icon">💾</span>
                            Sauvegarder
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="button-danger"
                        onClick={() => handleDeleteLogo(logo.id)}
                        disabled={updatingId === logo.id}
                        title="Supprimer ce logo"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default TrustedLogosPage;